import axios from "axios";
import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Category, fetchCategoryTree } from "../../api/categories";
import { config } from "../../config";
import "./Upload.css";

// A flattened representation of a tree node for rendering with indentation.
type TreeItem = {
  category: Category;
  depth: number;
  hasChildren: boolean;
};

function Upload() {
  const [title, setTitle] = useState("");
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [tree, setTree] = useState<Category[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const [status, setStatus] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const [uploadedImageUrl, setUploadedImageUrl] = useState("");

  useEffect(() => {
    fetchCategoryTree()
      .then(data => {
        setTree(Array.isArray(data) ? data : []);
      })
      .catch((error) => console.error("Failed to load categories:", error));
  }, []);

  const flatCategories = flattenTree(tree).map((item) => item.category);

  /**
   * Flatten the category tree into an ordered list of nodes with their depth,
   * so we can render each level indented below its parent.
   */
  function flattenTree(nodes: Category[], depth = 0): TreeItem[] {
    if (!Array.isArray(nodes)) {
      nodes = [];
    }
    let items: TreeItem[] = [];
    nodes.forEach((node) => {
      const children = node.children ?? [];
      items.push({ category: node, depth, hasChildren: children.length > 0 });
      if (children.length > 0) {
        items = items.concat(flattenTree(children, depth + 1));
      }
    });
    return items;
  }

  /**
   * Collect a category and all of its ancestors (walk up the parent hierarchy).
   * Used so that selecting a sub-leaf also selects every upper level.
   */
  function getSelfAndAncestors(id: number, all: Category[]): number[] {
    const ids = new Set<number>();
    const find = (currentId: number): Category | undefined => all.find((c) => c.id === currentId);
    let current = find(id);
    while (current) {
      ids.add(current.id);
      if (current.parentId == null) break;
      current = find(current.parentId);
    }
    return Array.from(ids);
  }

  /**
   * Collect a category and all of its descendants (walk down the tree).
   * Used so that deselecting a parent removes every nested child as well.
   */
  function getSelfAndDescendants(id: number, all: Category[]): number[] {
    const ids = new Set<number>([id]);
    const collect = (parentId: number) => {
      const children = all.filter((c) => c.parentId === parentId);
      children.forEach((child) => {
        ids.add(child.id);
        collect(child.id);
      });
    };
    collect(id);
    return Array.from(ids);
  }

  const handleCategoryToggle = (id: number) => {
    setSelectedCategoryIds((prev) => {
      const isSelected = prev.includes(id);
      if (isSelected) {
        const toRemove = getSelfAndDescendants(id, flatCategories);
        const remaining = prev.filter((c) => !toRemove.includes(c));
        return remaining.filter(
          (c) =>
            c === id ||
            getSelfAndDescendants(c, flatCategories).some((d) => d !== c && remaining.includes(d))
        );
      } else {
        const toAdd = getSelfAndAncestors(id, flatCategories);
        return Array.from(new Set([...prev, ...toAdd]));
      }
    });
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] ?? null;
    setFile(selectedFile);
    setStatus(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus(null);

    if (!file) {
      setStatus({ type: "error", message: "Please select an image file to upload." });
      return;
    }

    // Check file size (20MB limit)
    if (file.size > 20 * 1024 * 1024) {
      setStatus({ type: "error", message: "File size exceeds 20MB limit." });
      return;
    }

    const token = localStorage.getItem("token") || "";

    if (!token) {
      navigate("/login");
      return;
    }

    // Check for HTML tags in token (JWT tokens shouldn't contain '<' or '>')
    if (token.includes('<') || token.includes('>')) {
      localStorage.removeItem("token");
      navigate("/login");
      return;
    }

    // Check JWT structure (exactly two dots)
    if (token.split('.').length !== 3) {
      localStorage.removeItem("token");
      navigate("/login");
      return;
    }

    const cleanedToken = token.replace(/[^-A-Za-z0-9_.=]/g, '');
    if (cleanedToken === '') {
      localStorage.removeItem("token");
      navigate("/login");
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title);
      formData.append('location', location);
      formData.append('description', description);
      // The backend expects each category id as a separate "categoryIds" part.
      selectedCategoryIds.forEach((id) => formData.append('categoryIds', String(id)));

      const response = await axios.post(`${config.apiBaseUrl}/images`, formData, {
        headers: {
          Authorization: `Bearer ${cleanedToken}`,
        },
      });

      // The API returns the saved Image entity; build a public preview URL
      // from its fileName (large variant is served at /pics/large/l_<fileName>).
      const savedImage = response.data;
      if (!savedImage?.fileName) {
        throw new Error("Server did not return the uploaded image information.");
      }
      const imageUrl = `${config.picsBaseUrl}/large/l_${savedImage.fileName}`;
      setUploadedImageUrl(imageUrl);
      setStatus({
        type: "success",
        message: `Image uploaded successfully${savedImage.id != null ? ` (id: ${savedImage.id})` : ""}.`,
      });
    } catch (error: any) {
      console.error("Upload failed:", error);
      // Confirm the session before clearing it. An upload-specific 401 can be
      // produced by the API error handler and should remain visible here.
      if (error.response?.status === 401) {
        try {
          await axios.get(`${config.apiBaseUrl}/user/me`, {
            headers: { Authorization: `Bearer ${cleanedToken}` },
          });
        } catch (sessionError: any) {
          if (sessionError.response?.status === 401 || sessionError.response?.status === 400) {
            localStorage.removeItem("token");
            window.dispatchEvent(new Event("auth-state-changed"));
            navigate("/login");
            return;
          }
        }
      }
      let errorMessage = "Upload failed. Please try again with a valid image file.";
      if (error.response) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.body?.detail) {
          // Spring ProblemDetail format: { body: { detail: "..." } }
          errorMessage = error.response.data.body.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      setStatus({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const treeItems = flattenTree(tree);

  return (
    <main className="upload-page">
      <section className="upload-card">
        <h1>Upload a Photo</h1>
        <p>Upload a new picture and the server will save resized versions in small, medium, and large folders.</p>

        <form className="upload-form" onSubmit={handleSubmit}>
          <label>
            Image file
            <input type="file" accept="image/*" onChange={handleFileChange} />
          </label>

          <label>
            Title
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Photo title" />
          </label>

          <label>
            Location
            <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Location or place" />
          </label>

          <label>
            Description
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
            />
          </label>

          <fieldset className="category-fieldset">
            <legend>Categories</legend>
            {treeItems.length === 0 ? (
              <div className="category-empty">No categories available.</div>
            ) : (
              <div className="category-tree">
                {treeItems.map((item) => (
                  <label
                    key={item.category.id}
                    className="category-tree-node"
                    style={{ paddingLeft: item.depth * 22 }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCategoryIds.includes(item.category.id)}
                      onChange={() => handleCategoryToggle(item.category.id)}
                    />
                    <span className={item.hasChildren ? "category-node-name is-parent" : "category-node-name"}>
                      {item.category.name}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </fieldset>

          {status && <div className={`upload-message ${status.type}`}>{status.message}</div>}

          <div className="upload-actions">
            <button type="submit" disabled={isSubmitting} className="btn primary">
              {isSubmitting ? "Uploading..." : "Upload Photo"}
            </button>
            <button type="button" className="btn secondary" onClick={() => navigate("/")}>
              Back to Gallery
            </button>
          </div>
        </form>
        {status && (
          <div className={`status ${status.type}`}>
            {status.message}
          </div>
        )}
        {uploadedImageUrl && (
          <div className="uploaded-image">
            <img src={uploadedImageUrl} alt="Uploaded" />
            <p>Image uploaded successfully!</p>
          </div>
        )}
      </section>
    </main>
  );
}

export default Upload;
